import { HttpErrorResponse } from "@angular/common/http";

import { BankBatch } from "@core/models/bank-batch-model";
import { BankCollectionBatch } from "@core/models/bank-collection-batch";
import { BankTransactionMatch } from "@core/models/bank-transaction-match.model";
import { CollectionTransaction } from "@core/models/collection-transaction.model";
import { DisbursementTransactionAction } from "@core/models/disbursement-transaction-action.model";
import { ExtendedGfhAccount } from "@core/models/extended-gfh-account.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { PendingTransaction } from "@core/models/pending-transaction.model";
import { Search } from "@core/models/search.model";
import { Transaction } from "@core/models/transaction.model";
import { TableSorting } from "@custom-types/sorting";
import { createAction, props } from "@ngrx/store";

export const LOAD_BANK_BATCHES = createAction(
  "[Transaction] Load Bank Batches",
  props<{ selectedBatchId?: string; sorting?: TableSorting }>()
);

export const SET_BANK_BATCHES = createAction(
  "[Transaction] Set Bank Batches",
  props<{ bankBatchList: Array<BankBatch> }>()
);

export const LOAD_FUNDS = createAction("[Transaction] Load Funds");

export const SET_FUNDS = createAction(
  "[Transaction] Set Funds",
  props<{ fundList: Array<FundWithRelated> }>()
);

export const SET_TRANSACTION_LOADING_STATUS = createAction(
  "[Transaction] Set Transaction Loading Status",
  props<{ loading: boolean }>()
);

export const SET_TRANSACTION_ERROR = createAction(
  "[Transaction] Set Transaction Error",
  props<{ error: HttpErrorResponse }>()
);

export const LOAD_TRANSACTIONS = createAction(
  "[Transaction] Load Transactions",
  props<{ searchItems: Search }>()
);

export const SET_TRANSACTIONS = createAction(
  "[Transaction] Set Transactions",
  props<{ transactionList: Array<Transaction> }>()
);

export const UPLOAD_CUSTOMER_STATEMENT = createAction(
  "[Transaction] Upload Customer Statement",
  props<{ document: File }>()
);

export const REVERT_TRANSACTION = createAction(
  "[Transaction] Revert Transaction",
  props<{ id: string; selectedBatchId: string }>()
);

export const SUPPRESS_TRANSACTION = createAction(
  "[Transaction] Suppress Transaction",
  props<{
    id: string;
    suppressed: boolean;
    version: string;
    selectedBatchId: string;
  }>()
);

export const LOAD_GFH_ACCOUNTS = createAction(
  "[Transaction] Load Gfh Accounts",
  props<{
    transaction?: Transaction;
    searchTerm?: string;
  }>()
);

export const SET_GFH_ACCOUNTS = createAction(
  "[Transaction] Set Gfh Accounts",
  props<{ gfhAccountList: Array<ExtendedGfhAccount> }>()
);

export const RESET_GFH_ACCOUNTS = createAction(
  "[Transaction] Reset Gfh Accounts"
);

export const UPDATE_TRANSACTION_MATCH = createAction(
  "[Transaction] Update Transaction Match",
  props<{
    bankTransactionMatch: BankTransactionMatch;
    matchId: string;
    selectedBatchId: string;
  }>()
);

export const SET_TRANSACTION_SEARCH_ITEMS = createAction(
  "[Transaction] Set Transaction Search Items",
  props<{ searchItems: Search | null }>()
);

export const LOAD_BANK_COLLECTION_BATCHES = createAction(
  "[Transaction] Load Bank Collection Batches",
  props<{ sorting?: TableSorting }>()
);

export const LOAD_BANK_RETRY_COLLECTION_BATCHES = createAction(
  "[Transaction] Load Bank Retry Collection Batches",
  props<{ sorting?: TableSorting }>()
);

export const LOAD_BANK_FAILED_COLLECTION_BATCHES = createAction(
  "[Transaction] Load Bank Failed Collection Batches",
  props<{ sorting?: TableSorting }>()
);

export const SET_BANK_COLLECTION_BATCHES = createAction(
  "[Transaction] Set Bank Collection Batches",
  props<{ bankCollectionBatchList: Array<BankCollectionBatch> }>()
);

export const SET_BANK_RETRY_COLLECTION_BATCHES = createAction(
  "[Transaction] Set Bank Retry Collection Batches",
  props<{ bankCollectionBatchList: Array<BankCollectionBatch> }>()
);

export const SET_BANK_FAILED_COLLECTION_BATCHES = createAction(
  "[Transaction] Set Bank Failed Collection Batches",
  props<{ bankCollectionBatchList: Array<CollectionTransaction> }>()
);

export const DOWNLOAD_COLLECTION = createAction(
  "[Transaction] Download Collection",
  props<{ id: string }>()
);

export const EXPORT_PERIODIC_COLLECTION = createAction(
  "[Transaction] Export Periodic Collection"
);

export const EXPORT_FAILED_COLLECTION = createAction(
  "[Transaction] Export Failed Collection",
  props<{ collectionsToRetry: Array<string> }>()
);

export const SET_COLLECTION_LOADING_STATUS = createAction(
  "[Transaction] Set Collection Loading Status",
  props<{ loading: boolean }>()
);

export const SET_RETRY_COLLECTION_LOADING_STATUS = createAction(
  "[Transaction] Set Retry Collection Loading Status",
  props<{ loading: boolean }>()
);

export const SET_FAILED_COLLECTION_LOADING_STATUS = createAction(
  "[Transaction] Set Failed Collection Loading Status",
  props<{ loading: boolean }>()
);

export const SUPPRESS_COLLECTION_TRANSACTION = createAction(
  "[Transaction] Suppress Collection Transaction",
  props<{ id: string; version: string }>()
);

export const MAKE_COLLECTIONS_DEFINITIVE = createAction(
  "[Transaction] Make Collections Definitive"
);

export const LOAD_DISBURSEMENTS = createAction(
  "[Transaction] Load Disbursements",
  props<{ sorting?: TableSorting }>()
);

export const SET_DISBURSEMENTS = createAction(
  "[Transaction] Set Disbusements",
  props<{ disbursementList: Array<PendingTransaction> }>()
);

export const SET_DISBURSEMENT_LOADING_STATUS = createAction(
  "[Transaction] Set Disbursement Loading Status",
  props<{ loading: boolean }>()
);

export const SET_SELECTED_DISBURSEMENT = createAction(
  "[Transaction] Set Selected Disbursement",
  props<{ selectedDisbursement: boolean }>()
);

export const CREATE_DISBURSEMENT_BATCH = createAction(
  "[Transaction] Create Disbursement Batch",
  props<{
    disbursementTransactionActionList: Array<DisbursementTransactionAction>;
    selectedDisbursementList: Array<PendingTransaction>;
  }>()
);

export const CREATE_DISBURSEMENT_REPORT = createAction(
  "[Transaction] Create Disbursement Report"
);

export const PROCESS_DISBURSEMENT = createAction(
  "[Transaction] Process Disbursement",
  props<{ disbursementTransactionAction: DisbursementTransactionAction }>()
);
